import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import './tags.css';

/** Dynamic modal with basic UI
 * @param {array} value - array of values/tags or default empty []
 * @param {Function} onChange - callback function when confirming changes in input
 * @param {string} inputName - name of input
 * @param {string} placeholderText - custom placeholder of input field
 * @param {object} customStyle - custom styles for input field
 * @param {string} helpText - custom help message
 * @param {boolean} showDelete - to remove delete option
 */

const Tag = ({ children, ...props }) => (
  <span className="tag" {...props}>
    {children}
  </span>
);

const Delete = ({ onClick }) => <button className="delete" onClick={onClick}></button>;
const Help = ({ children }) => <span className="help">{children}</span>;

const TagsInput = ({ value, onChange, inputName, placeholderText, customStyle, helpText, headText, showDelete = true, disabled = false }) => {
  const [newTag, setNewTag] = useState('');

  // Helper function to process comma-separated tags and add them
  const addTags = useCallback(
    (tags) => {
      if (disabled) return;
      const uniqueTags = [...new Set(tags.map((tag) => tag.trim()))].filter(
        (tag) => tag !== '' && !value.includes(tag)
      );
      if (uniqueTags.length > 0) {
        onChange([...value, ...uniqueTags]);
      }
    },
    [value, onChange, disabled]
  );

  const handleChange = useCallback((e) => {
    setNewTag(e.target.value);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if ((e.key === 'Enter' || e.key === ',') && newTag.trim() !== '') {
        e.preventDefault(); // Prevent creating a new line
        addTags([newTag.trim()]);
        setNewTag('');
      }
    },
    [newTag, addTags]
  );

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');
      const tags = pastedData.split(',');
      addTags(tags);
    },
    [addTags]
  );

  const handleRemoveTag = useCallback(
    (tagToRemove) => {
      if (disabled) return;
      onChange(value.filter((tag) => tag !== tagToRemove));
    },
    [value, onChange, disabled]
  );

  return (
    <div className="tags-input-wrapper">
      {headText && <span>{headText}</span>}
      <div className="tags-input">
        {value.map((tag, index) => (
          <Tag key={index}>
            {tag}
            {showDelete && <Delete onClick={() => handleRemoveTag(tag)} />}
          </Tag>
        ))}
        {!disabled && (
          <input
            type="text"
            value={newTag}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholderText}
            name={inputName}
            style={customStyle}
          />
        )}
      </div>
      {helpText && <Help>{helpText}</Help>}
    </div>
  );
};

TagsInput.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  inputName: PropTypes.string,
  placeholderText: PropTypes.string,
  customStyle: PropTypes.object,
  helpText: PropTypes.string,
  headText: PropTypes.string,
  showDelete: PropTypes.bool,
  disabled: PropTypes.bool,
};

TagsInput.defaultProps = {
  inputName: '',
  placeholderText: 'Enter new tag',
  customStyle: {},
  helpText: "Hit 'Enter' or paste a comma-separated list to add tags",
  headText: '',
  disabled: false,
};

export default TagsInput;
